// SPDX-License-Identifier: GPL-3.0-or-later
// The floor zones of the table: soft, low-saturation colour under each area, so that the floor is
// not plain black. A zone adds light to the floor (additive blending). White text on the floor
// stays white, and the floor stays dark enough for the text rule: at least 4.5 to 1
// (WCAG 1.4.3) between white and the floor of any zone, also where zones overlap.
// The sizes are table units. The colours come from palette.ts.

import { TOKEN_HEX } from "./palette.ts";

/** One floor zone: a soft box with a centre, a size, a token colour and a strength. */
export interface FloorZone {
  readonly name: string;
  /** Centre in table units (x right, y down). */
  readonly x: number;
  readonly y: number;
  /** Size in table units. */
  readonly w: number;
  readonly h: number;
  readonly hex: number;
  /** Strength from 0 to 1. The zone adds `hex` times this to the floor. */
  readonly alpha: number;
}

/** The text contrast that the floor must keep against white text (WCAG 1.4.3, normal text). */
export const MIN_TEXT_CONTRAST = 4.5;

/** The zones of the table "SigmaShake Pinball", top to bottom. */
export const FLOOR_ZONES: readonly FloorZone[] = [
  { name: "top-lanes", x: 500, y: 170, w: 460, h: 190, hex: TOKEN_HEX.force, alpha: 0.34 },
  { name: "bumper-pit", x: 495, y: 440, w: 420, h: 330, hex: TOKEN_HEX.ask, alpha: 0.3 },
  { name: "token-bank", x: 190, y: 920, w: 280, h: 360, hex: TOKEN_HEX.allow, alpha: 0.32 },
  { name: "saucer-shots", x: 490, y: 940, w: 220, h: 380, hex: TOKEN_HEX.block, alpha: 0.3 },
  { name: "deny-bank", x: 775, y: 920, w: 280, h: 360, hex: TOKEN_HEX.force, alpha: 0.32 },
  { name: "mode-lamps", x: 490, y: 1360, w: 320, h: 240, hex: TOKEN_HEX.ask, alpha: 0.28 },
  { name: "left-lanes", x: 190, y: 1610, w: 300, h: 400, hex: TOKEN_HEX.block, alpha: 0.28 },
  { name: "right-lanes", x: 810, y: 1610, w: 300, h: 400, hex: TOKEN_HEX.force, alpha: 0.3 },
  { name: "saver", x: 500, y: 1730, w: 280, h: 200, hex: TOKEN_HEX.allow, alpha: 0.28 },
];

/** An sRGB channel (0 to 255) as linear light (0 to 1). */
function linear(channel: number): number {
  const c = channel / 255;
  return c <= 0.04045 ? c / 12.92 : ((c + 0.055) / 1.055) ** 2.4;
}

/** Relative luminance of a hex colour (WCAG 2.x). */
export function luminanceOf(hex: number): number {
  const r = linear((hex >> 16) & 0xff);
  const g = linear((hex >> 8) & 0xff);
  const b = linear(hex & 0xff);
  return 0.2126 * r + 0.7152 * g + 0.0722 * b;
}

/** Contrast ratio of two hex colours (WCAG 2.x). The result is from 1 to 21. */
export function contrastRatio(a: number, b: number): number {
  const la = luminanceOf(a);
  const lb = luminanceOf(b);
  return (Math.max(la, lb) + 0.05) / (Math.min(la, lb) + 0.05);
}

/** The floor colour that a set of zones gives over a black floor. Each channel clamps at 255. */
export function floorHexOf(zones: readonly FloorZone[]): number {
  let r = 0;
  let g = 0;
  let b = 0;
  for (const zone of zones) {
    r += ((zone.hex >> 16) & 0xff) * zone.alpha;
    g += ((zone.hex >> 8) & 0xff) * zone.alpha;
    b += (zone.hex & 0xff) * zone.alpha;
  }
  const c = (v: number): number => Math.min(255, Math.round(v));
  return (c(r) << 16) | (c(g) << 8) | c(b);
}

/** True when the boxes of two zones overlap. */
function overlaps(a: FloorZone, b: FloorZone): boolean {
  return Math.abs(a.x - b.x) < (a.w + b.w) / 2 && Math.abs(a.y - b.y) < (a.h + b.h) / 2;
}

/**
 * The worst floor colour of a zone: the zone and every zone whose box overlaps it, added up. This
 * is brighter than any real point, so a contrast test on it is safe.
 */
export function worstFloorHex(zone: FloorZone, zones: readonly FloorZone[] = FLOOR_ZONES): number {
  return floorHexOf(zones.filter((other) => other === zone || overlaps(zone, other)));
}
